import { useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';

/**
 * Theme wrapper around the Engrain Sightmap embed.
 * Drop-shadow, height cap, and the click-to-interact overlay live here so
 * the block embed stays untouched. The iframe would otherwise capture
 * every scroll and touch that lands on it. After the first click, the
 * overlay stays off for the rest of the visit.
 */
export function InteractiveMapSection() {
  const mapSlot = useBlockSlot('interactivemapsection.map_block', {
    defaultValue: 'engrain-sitemap',
  });
  const [isInteractive, setIsInteractive] = useState(false);

  return (
    <section className="ims-section">
      <div className="ims-shadow">
        <div className="ims-frame">
          <EditableToken
            path="interactivemapsection.map_block"
            as="div"
            className="ims-block"
          >
            {mapSlot ? (
              <SectionContextProvider sectionId={mapSlot.sectionId}>
                <mapSlot.BlockComponent sectionId={mapSlot.sectionId} />
              </SectionContextProvider>
            ) : (
              <div className="ims-empty">No block selected</div>
            )}
          </EditableToken>
          {mapSlot && !isInteractive ? (
            <button
              type="button"
              className="ims-overlay"
              onClick={() => setIsInteractive(true)}
              aria-label="Click to interact with the map"
            >
              <span className="ims-overlay-cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all">
                Click to interact
              </span>
            </button>
          ) : null}
        </div>
      </div>

      <style>{`
        .ims-section {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
          box-sizing: border-box;
          background: var(--vibe-color-background, #ffffff);
          padding: 2.5rem 1.25rem;
          --ims-map-height: min(78vh, 720px);
        }
        @media (min-width: 768px) {
          .ims-section {
            padding: 3rem 1.5rem;
            --ims-map-height: min(82vh, 900px);
          }
        }
        @media (min-width: 1200px) {
          .ims-section { --ims-map-height: min(88vh, 980px); }
        }

        /* Shadow sits on the wrapper, not the block, so it is visible on
           all four sides of a full-bleed embed without clipping the iframe. */
        .ims-shadow {
          max-width: min(80rem, 100%);
          min-width: 0;
          margin: 0 auto;
          box-shadow:
            0 -14px 40px rgba(0, 0, 0, 0.12),
            0 22px 56px rgba(0, 0, 0, 0.18),
            0 6px 18px rgba(0, 0, 0, 0.08);
        }

        .ims-frame {
          position: relative;
          height: var(--ims-map-height);
          max-width: 100%;
          min-width: 0;
          overflow: hidden;
          overscroll-behavior-x: none;
          touch-action: pan-y;
          background: #ffffff;
        }

        .ims-block,
        .ims-block :is(section, iframe, .rrac_interactive_leasing_view) {
          display: block;
          box-sizing: border-box;
          width: 100%;
          max-width: 100% !important;
          min-width: 0 !important;
          height: 100% !important;
          max-height: 100% !important;
          min-height: 0 !important;
        }

        .ims-empty {
          font-family: var(--vibe-font-body);
          color: var(--vibe-color-text-muted);
          text-align: center;
          padding: 2rem 1rem;
        }

        /* Section-owned overlay. Sits on the frame, never on the block, so
           the embed keeps its own markup and the iframe cannot steal scroll
           until the visitor opts in. */
        .ims-overlay {
          position: absolute;
          inset: 0;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0;
          padding: 0;
          border: 0;
          background: rgba(0, 0, 0, 0.18);
          cursor: pointer;
          touch-action: pan-y;
          transition: background 0.2s ease;
        }
        .ims-overlay:hover,
        .ims-overlay:focus-visible {
          background: rgba(0, 0, 0, 0.28);
        }
        .ims-overlay:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: -2px;
        }
        /* Mirrors the site-wide CTA pill (see Header's CTA recolor sheet).
           Hover is keyed off the overlay button because the pill has
           pointer-events: none. */
        .ims-overlay-cta {
          pointer-events: none;
          background: var(--vibe-color-surface);
          border: 1.5px solid var(--vibe-color-surface);
          border-radius: 9999px;
          color: var(--gr-cta-fg, var(--vibe-color-text-on-primary, #fff));
        }
        .ims-overlay:hover .ims-overlay-cta,
        .ims-overlay:focus-visible .ims-overlay-cta {
          background: color-mix(in srgb, var(--vibe-color-surface), black 12%);
          border-color: color-mix(in srgb, var(--vibe-color-surface), black 12%);
          transform: translateY(-1px);
          box-shadow:
            0 2px 6px rgba(0,0,0,0.1),
            0 4px 16px color-mix(in srgb, var(--vibe-color-surface) 25%, transparent);
        }
        .ims-overlay:active .ims-overlay-cta {
          transform: translateY(0);
          box-shadow: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .ims-overlay-cta { transition: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'InteractiveMapSection',
  displayName: 'Interactive Map Section',
  tokens: {
    'interactivemapsection.map_block': {
      label: 'Map Block',
      type: 'block',
      defaultValue: 'engrain-sitemap',
      acceptedBlockCategories: ['Integrations', 'Embeds'],
    },
  },
  slotGroups: {},
  collectionBindings: {},
};

(InteractiveMapSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default InteractiveMapSection;
